import { ReactNode } from "react";

type Props = {
  /** 0-100 */
  progress: number;
  /** In viewBox units: px when `size` is set, percent of diameter otherwise */
  strokeWidth: number;
  pathColor: string;
  trailColor?: string;
  /** Fixed pixel size; fills the parent when omitted */
  size?: number;
  /** Visible degrees of the ring; below 360 renders a gauge-style arc */
  arcAngle?: number;
  /** Arc start, degrees clockwise from 12 o'clock */
  rotation?: number;
  /** Dot rendered at the progress tip, in viewBox units */
  pointerRadius?: number;
  className?: string;
  children?: ReactNode;
};

function ProgressRing({
  progress,
  strokeWidth,
  pathColor,
  trailColor = "transparent",
  size,
  arcAngle = 360,
  rotation = 0,
  pointerRadius = 0,
  className,
  children,
}: Props) {
  const box = size ?? 100;
  const center = box / 2;
  const radius = center - Math.max(strokeWidth, pointerRadius * 2) / 2;
  const circumference = 2 * Math.PI * radius;
  const arcLength = (circumference * arcAngle) / 360;
  const clampedProgress = Math.min(100, Math.max(0, progress));

  return (
    <div
      className={className}
      style={{ position: "relative", width: size ?? "100%", height: size ?? "100%" }}
    >
      <svg
        viewBox={`0 0 ${box} ${box}`}
        style={{ display: "block", width: "100%", height: "100%" }}
      >
        {/* SVG dashes start at 3 o'clock; shift so `rotation` is measured from 12 o'clock */}
        <g transform={`rotate(${rotation - 90} ${center} ${center})`}>
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={trailColor}
            strokeWidth={strokeWidth}
            strokeDasharray={`${arcLength} ${circumference}`}
            strokeLinecap="round"
          />
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={pathColor}
            strokeWidth={strokeWidth}
            strokeDasharray={`${arcLength} ${circumference}`}
            strokeDashoffset={arcLength * (1 - clampedProgress / 100)}
            strokeLinecap="round"
            style={{ transition: "stroke-dashoffset 0.3s ease" }}
          />
          {pointerRadius > 0 && (
            <circle
              cx={center + radius}
              cy={center}
              r={pointerRadius}
              fill={pathColor}
              transform={`rotate(${(arcAngle * clampedProgress) / 100} ${center} ${center})`}
            />
          )}
        </g>
      </svg>

      {children && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export default ProgressRing;
